<template>
  <div class="row">
    <div class="col-12">
      <div class="col-md-12">
        <create-edit-tool-form v-if="showCreateToolForm" @close="closeCreateToolForm" @refresh-data="fetchData" @refresh-tooltypesdata="fetchToolTypesData"></create-edit-tool-form>
      </div>
      <!-- SHOPPING CART: RentLoanForm for multi-rentals -->
      <div v-if="enableMultiRentals" class="col-md-12">
        <rent-loan-form v-if="showRentLoanForm" :tool-ids="toolsInCart.join(',')" :multi-tool="false" @close="closeRentLoanForm" @refreshData="finishRentLoan"></rent-loan-form>
      </div>
      <!-- SHOPPING CART: Updated card with cart toggle -->
      <card card-body-classes="table-full-width" :title="cardTitle" :on-add-object="userRole !== 'User' && !cartActive ? openCreateToolForm : null" :on-custom-method="userRole !== 'User' && enableMultiRentals ? toggleCart : null" :custom-method-icon-counter="toolsInCart.length" :custom-method-icon-with-counter="cartIcon" :custom-method-icon="cartCloseIcon">
        <template v-if="enableExcelExport && userRole !== 'User'" #under-header>
          <div style="padding: 0 0 8px;">
            <el-button :icon="Download" size="small" @click="showExportDialog = true">{{ $t('dataExport.exportTools') }}</el-button>
          </div>
        </template>
        <div v-if="toolsDataReady">
          <div>
            <div class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap">
            <div class="col-12 col-md-4 col-sm-6 mb-2">
              <el-input
                v-model="searchQuery"
                class="search-input"
                clearable
                :prefix-icon="Search"
                :placeholder="$t('common.searchRecords')"
                aria-controls="datatables"
                @keyup="search"
                @clear="handleClearSearch"
              />
            </div>
              <!-- SHOPPING CART: Loan/Rent button when cart is active -->
              <div v-if="cartActive && !isMobile">
                <material-button color="dark" @click="openRentLoanForm">{{ rentLoanButtonText }}</material-button>
              </div>
              <!-- SHOPPING CART: Mobile cart button -->
              <div v-if="isMobile && cartActive" class="col-9 status-select">
                <material-button full-width color="dark" @click="openRentLoanForm">{{ rentLoanButtonText }}</material-button>
              </div>
            </div>
            <div v-if="isMobile && !cartActive" class="row mb-2">
              <div class="col-6 mb-2">
                <el-select v-model="statusFilter" :placeholder="$t('common.status')" class="w-100" @change="setStatusFilter">
                  <el-option v-for="status in statusOptions" :key="status.id" :label="status.status" :value="status.status"></el-option>
                </el-select>
              </div>
              <div class="col-6 mb-2">
                <el-select v-model="toolTypeFilter" filterable :placeholder="$t('common.type')" class="w-100" @change="setToolTypeFilter">
                  <el-option v-for="toolType in toolTypeOptions" :key="toolType.id" :label="toolType.type" :value="toolType.type"></el-option>
                </el-select>
              </div>
              <div v-if="allowLocationFilteringForTools" class="col-12 mb-2">
                <el-select v-model="locationFilter" filterable :placeholder="$t('locations.location')" class="w-100" @change="setLocationFilter">
                  <el-option v-for="location in locationOptions" :key="location.id" :label="location.name" :value="location.name"></el-option>
                </el-select>
              </div>
            </div>
            <!-- Desktop Table -->
            <div v-if="toolTypeOptionsAvailable">
              <el-table v-if="!isMobile" :data="tools" @row-click="goToTool" @sort-change="handleSortChange">
                <el-table-column prop="image" :label="$t('common.image')" width="80">
                  <template #default="{ row }">
                    <img class="img-fluid shadow" :src="row.image" alt="Tool Image" />
                  </template>
                </el-table-column>
                <el-table-column v-for="column in filteredTableColumns" :key="column.label" class="mobile-stack" :min-width="column.minWidth" :prop="column.prop" :label="column.label" :sortable="column.sortable || false">
                  <template #default="{ row }">
                    <div :style="column.prop === 'customerToolId' ? { whiteSpace: 'nowrap' } : {}">
                      {{ row[column.prop] }}
                      <br v-if="column.prop === 'status' && row.reservingUser !== ''" />
                      <span v-if="column.prop === 'status' && row.reservingUser !== ''" class="status-info">{{$t('common.user')}}: </span>
                      <a v-if="column.prop === 'status' && row.reservingUser !== ''" class="clickable-link-small-font" @click.stop="navigateToUserProfile(row.reservingUserId)">{{ row.reservingUser}}</a>
                      <br v-if="column.prop === 'status' && row.project !== ''"/>
                      <span v-if="column.prop === 'status' && row.project !== ''" class="status-info">{{$t('common.project')}}: </span>
                      <a v-if="column.prop === 'status' && row.project !== ''" class="clickable-link-small-font" @click.stop="navigateToProject(row.projectId)">{{ row.project}}</a>
                      <br v-if="column.prop === 'status' && row.car !== ''"/>
                      <span v-if="column.prop === 'status' && row.car !== ''" class="status-info">{{$t('cars.car')}}: </span>
                      <a v-if="column.prop === 'status' && row.car !== ''" class="clickable-link-small-font" @click.stop="navigateToCar(row.carId)">{{ row.car}} - {{ row.carLicensePlate}}</a>
                      <br v-if="column.prop === 'status' && row.externalUser !== ''"/>
                      <span v-if="column.prop === 'status' && row.externalUser !== ''" class="status-info">{{$t('tools.loanedRentedTo')}}: {{ row.externalUser}}</span>
                      <br v-if="column.prop === 'status' && row.totalUse !== ''"/>
                      <span v-if="column.prop === 'status' && row.totalUse !== ''" class="status-info">{{$t('tools.totalUsed')}}: {{ row.totalUse}}</span>
                    </div>
                  </template>
                  <template #header>
                    <div v-if="column.label === $t('common.status')">
                      <el-select v-model="statusFilter" :class="selectClass" :placeholder="$t('common.status')" @change="setStatusFilter">
                        <el-option v-for="status in statusOptions" :key="status.id" :class="selectClass" :label="status.status" :value="status.status"></el-option>
                      </el-select>
                    </div>
                    <div v-else-if="column.label === $t('common.type')">
                      <el-select v-model="toolTypeFilter" filterable :class="selectClass" :placeholder="$t('common.type')" @change="setToolTypeFilter">
                        <el-option v-for="toolType in toolTypeOptions" :key="toolType.id" :class="selectClass" :label="toolType.type" :value="toolType.type"></el-option>
                      </el-select>
                    </div>
                    <div v-else-if="column.label === $t('locations.location') && allowLocationFilteringForTools">
                      <el-select v-model="locationFilter" filterable :class="selectClass" :placeholder="$t('locations.location')" @change="setLocationFilter">
                        <el-option v-for="location in locationOptions" :key="location.id" :label="location.name" :value="location.name"></el-option>
                      </el-select>
                    </div>
                    <div v-else>{{ column.label }}</div>
                  </template>
                </el-table-column>
                <el-table-column v-if="userRole !== 'User'" :min-width="120" align="right" label="">
                  <template #default="{ row, $index }">
                    <!-- SHOPPING CART: Add to cart button -->
                    <el-button v-if="shouldShowAddToCartButton(row)" type="success" circle class="me-1" @click.stop="addToCart($index, row)">
                      <el-icon><ShoppingCart /></el-icon>
                    </el-button>
                    <!-- SHOPPING CART: Remove from cart button -->
                    <el-button v-if="shouldShowRemoveFromCartButton(row)" type="danger" circle class="me-1" @click.stop="removeFromCart($index, row)">
                      <el-icon><Close /></el-icon>
                    </el-button>
                    <!-- Edit button (hide when cart active) -->
                    <el-button v-if="!cartActive" type="warning" circle @click.stop="handleEdit(row)">
                      <el-icon><Edit /></el-icon>
                    </el-button>
                    <!-- Delete button (hide when cart active) -->
                    <el-button v-if="!cartActive" type="danger" circle @click.stop="handleDelete(row)">
                      <el-icon><Delete /></el-icon>
                    </el-button>
                  </template>
                </el-table-column>
              </el-table>
              <!-- Mobile Table -->
              <el-table v-else :data="tools" @row-click="goToTool">
                <el-table-column :label="$t('common.image')">
                  <template #default="{ row }">
                    <img class="img-fluid shadow" :src="row.image" alt="Tool Image"/>
                  </template>
                </el-table-column>
                <el-table-column :label="$t('common.info')" min-width="150">
                  <template #default="{ row, $index }">
                    <div>
                      <div><strong>{{$t('common.customerToolId')}}:</strong> {{ row.customerToolId }}</div>
                      <div class="mobile-info-row"><strong class="mobile-label">{{$t('common.name')}}:</strong> <span class="truncate-text">{{ row.name }}</span></div>
                      <div v-if="row.locationName" class="mobile-info-row"><strong class="mobile-label">{{$t('locations.location')}}:</strong> <span class="truncate-text">{{ row.locationName }}</span></div>
                      <div><strong>{{$t('common.status')}}:</strong> {{ row.status }}</div>
                      <div v-if="row.reservingUser !== ''"><strong>{{$t('common.user')}}:</strong> <a class="clickable-link" @click.stop="navigateToUserProfile(row.reservingUserId)">{{ row.reservingUser }}</a></div>
                      <div v-if="row.project !== ''"><strong>{{$t('common.project')}}:</strong> <a class="clickable-link" @click.stop="navigateToProject(row.projectId)">{{ row.project }}</a></div>
                      <div v-if="row.car !== ''"><strong>{{$t('cars.car')}}:</strong> <a class="clickable-link" @click.stop="navigateToCar(row.carId)">{{ row.car }}</a></div>
                      <div v-if="row.externalUser !== ''"><strong>{{$t('tools.loanedRentedTo')}}:</strong> {{ row.externalUser }}</div>
                      <div v-if="row.totalUse !== ''"><strong>{{$t('tools.totalUsed')}}:</strong> {{ row.totalUse }}</div>
                      <div v-if="userRole !== 'User'" class="mt-2">
                        <!-- SHOPPING CART: Mobile cart buttons -->
                        <el-button v-if="shouldShowAddToCartButton(row)" type="success" circle class="me-1" @click.stop="addToCart($index, row)">
                          <el-icon><ShoppingCart /></el-icon>
                        </el-button>
                        <el-button v-if="shouldShowRemoveFromCartButton(row)" type="danger" circle class="me-1" @click.stop="removeFromCart($index, row)">
                          <el-icon><Close /></el-icon>
                        </el-button>
                        <el-button v-if="!cartActive" type="warning" circle @click.stop="handleEdit(row)">
                          <el-icon><Edit /></el-icon>
                        </el-button>
                        <el-button v-if="!cartActive" type="danger" circle @click.stop="handleDelete(row)">
                          <el-icon><Delete /></el-icon>
                        </el-button>
                      </div>
                    </div>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>
          <div class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap pagination">
            <div class="">
              <p class="card-category">{{ $t('pagination.showing') }} {{ from + 1 }} {{ $t('pagination.to') }} {{ to }} {{ $t('pagination.of') }} {{ total }} {{ $t('pagination.entries') }}</p>
            </div>
            <material-pagination color="info" class="pagination-no-border">
              <material-pagination-item
                prev
                :disabled="pagination.currentPage === 1"
                @click="prevPage"
              />
              <material-pagination-item
                v-for="page in displayedPages"
                :key="page"
                :label="String(page)"
                :active="pagination.currentPage === page"
                @click="changePage(page)"
              />
              <material-pagination-item
                next
                :disabled="pagination.currentPage === totalPages"
                @click="nextPage"
              />
            </material-pagination>
          </div>
        </div>
      </card>
    </div>
  </div>

  <data-export-modal
    v-model:show="showExportDialog"
    type="tools"
    :tool-type-options="toolTypeOptions"
    :location-options="locationOptions"
  />
</template>
<script>
import api from "../../services/api";
import { jwtDecode } from "jwt-decode";
import Cookies from 'js-cookie';
import CreateEditToolForm from "./CreateEditToolForm.vue";
import RentLoanForm from "./RentLoanForm.vue"; // SHOPPING CART: Import RentLoanForm
import MaterialPagination from '../../components/MaterialPagination.vue';
import MaterialPaginationItem from '../../components/MaterialPaginationItem.vue';
import { ElTable, ElTableColumn, ElSelect, ElOption, ElInput, ElButton, ElIcon } from 'element-plus';
import { Edit, Delete, ShoppingCart, Close, Download } from '@element-plus/icons-vue'; // SHOPPING CART: Add ShoppingCart, Close icons
import swal from 'sweetalert2';
import { getCustomerConfig } from '../Settings/customerConfigHelper';
import MaterialButton from "../../components/MaterialButton.vue";
import Card from "../../components/Card.vue";
import { Search } from '@element-plus/icons-vue';
import { shallowRef } from 'vue';
import DataExportModal from '../../components/DataExportModal.vue';

export default {
  components: {
    CreateEditToolForm,
    RentLoanForm, // SHOPPING CART: Register component
    DataExportModal,
    MaterialPagination,
    MaterialPaginationItem,
    ElTable,
    ElTableColumn,
    ElSelect,
    ElOption,
    ElInput,
    ElButton,
    ElIcon,
    MaterialButton,
    Card,
    Edit,
    Delete,
    ShoppingCart, // SHOPPING CART: Register icon
    Close // SHOPPING CART: Register icon
  },
  data() {
    return {
      Search: shallowRef(Search),
      Download: shallowRef(Download),
      // SHOPPING CART: New data properties
      customerId: '',
      showRentLoanForm: false,
      enableMultiRentals: false,
      rentalsOnly: false,
      cartActive: false,
      toolsInCart: [],
      // Existing data properties
      toolsDataReady: false,
      cardTitle: '',
      isMobile: false,
      initalPage: true,
      pagination: {
        perPage: 10,
        currentPage: 1,
        total: 0
      },
      sortBy: null,
      sortDescending: false,
      tableColumns: [
        { prop: 'customerToolId', label: this.$t('common.customerToolId'), minWidth: 70, sortable: 'custom', sortField: 'customerToolId' },
        { prop: 'name', label: this.$t('common.name'), minWidth: 100, sortable: 'custom', sortField: 'name' },
        { prop: 'description', label: this.$t('common.description'), minWidth: 200 },
        { prop: 'toolTypeId', label: this.$t('common.type'), minWidth: 100 },
        { prop: 'status', label: this.$t('common.status'), minWidth: 150 },
        { prop: 'locationName', label: this.$t('locations.location'), minWidth: 100, vIf: () => this.allowLocationFilteringForTools }
      ],
      searchQuery: '',
      userRole: null,
      tools: [],
      showCreateToolForm: false,
      statusFilter: '',
      statusOptions: [
        { id: 0, status: this.$t('toolStatus.all') },
        { id: 1, status: this.$t('toolStatus.free') },
        { id: 2, status: this.$t('toolStatus.inUse') },
        { id: 3, status: this.$t('toolStatus.broken') },
        { id: 4, status: this.$t('toolStatus.outOfUse') },
        { id: 5, status: this.$t('toolStatus.loaned') },
        { id: 6, status: this.$t('toolStatus.rented') },
        { id: 7, status: this.$t('toolStatus.partlyUsed') },
        { id: 8, status: this.$t('toolStatus.fullyUsed') }
      ],
      toolTypeFilter: '',
      toolTypeOptions: [{ id: 0, type: this.$t('tools.toolTypeStatus') }],
      toolTypeOptionsAvailable: false,
      locationFilter: '',
      locationOptions: [{ id: 0, name: this.$t('locations.all') }],
      locationOptionsAvailable: false,
      allowLocationFilteringForTools: false,
      showExportDialog: false,
      enableExcelExport: false
    };
  },
  computed: {
    // SHOPPING CART: Button text based on config
    rentLoanButtonText() {
      if (this.rentalsOnly) {
        return this.$t('tools.rent');
      }
      return this.$t('tools.loanRent');
    },
    // SHOPPING CART: Cart icon when not active
    cartIcon() {
      if (!this.cartActive) {
        return 'shopping_cart';
      }
      return '';
    },
    // SHOPPING CART: Close icon when cart is active
    cartCloseIcon() {
      if (this.cartActive) {
        return 'close';
      }
      return '';
    },
    filteredTableColumns() {
      return this.tableColumns.filter(column => !column.vIf || column.vIf());
    },
    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    },
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },
    total() {
      return this.pagination.total;
    },
    totalPages() {
      if (this.total > 0) {
        return Math.ceil(this.total / this.pagination.perPage);
      }
      return 1;
    },
    displayedPages() {
      const pages = [];
      const maxPages = Math.min(5, this.totalPages);

      let startPage = Math.max(1, this.pagination.currentPage - Math.floor(maxPages / 2));
      let endPage = startPage + maxPages - 1;

      if (endPage > this.totalPages) {
        endPage = this.totalPages;
        startPage = Math.max(1, endPage - maxPages + 1);
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      return pages;
    },
    selectClass() {
      let darkMode = localStorage.getItem('darkMode');
      if (darkMode === null) {
        darkMode = 'false';
      }
      if (darkMode === 'false') {
        return 'select-standard';
      } else {
        return 'select-primary';
      }
    }
  },
  beforeMount() {
    this.checkMobile();
  },
  mounted() {
    const savedSearchQuery = Cookies.get('toolsSearchQuery');
    if (savedSearchQuery) {
      this.searchQuery = savedSearchQuery;
    } else {
      this.searchQuery = '';
    }
    const savedPage = Cookies.get('toolsPage');
    if (savedPage) {
      this.pagination.currentPage = Number(savedPage);
    } else {
      this.pagination.currentPage = 1;
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile);
  },
  created() {
    this.cardTitle = this.$t('tools.tools');
    window.addEventListener('resize', this.checkMobile);
    const token = Cookies.get('accessToken');
    const decodedToken = jwtDecode(token);
    this.userRole = decodedToken.role;
    this.customerId = decodedToken.CustomerId; // SHOPPING CART: Get customer ID

    if (window.innerWidth < 768) {
      this.statusFilter = this.$t('toolStatus.all');
      this.toolTypeFilter = this.$t('tools.toolTypeStatus');
    }

    // SHOPPING CART: Load multi-rentals config
    getCustomerConfig('EnableMultiRentals')
      .then(config => {
        this.enableMultiRentals = config === '' ? false : config;
        if (this.enableMultiRentals) {
          const storedTools = localStorage.getItem(this.customerId + '-toolsInCart');
          this.toolsInCart = storedTools ? JSON.parse(storedTools) : [];
        }
      });

    // SHOPPING CART: Load rentals-only config
    getCustomerConfig('EnableRentalsOnly')
      .then(config => {
        this.rentalsOnly = config === '' ? false : config;
      });

    getCustomerConfig('AllowLocationFilteringForTools')
      .then(config => {
        this.allowLocationFilteringForTools = config === '' ? false : config;
        if (this.allowLocationFilteringForTools) {
          return this.fetchLocationsData();
        }
      });

    getCustomerConfig('DefaultPageSize')
      .then(config => {
        if (config === '') {
          this.pagination.perPage = 10;
        } else {
          this.pagination.perPage = config;
        }
        this.fetchToolTypesData();
      });

    getCustomerConfig('EnableExcelExport')
      .then(config => { this.enableExcelExport = config === '' ? false : config; });
  },
  methods: {
    // SHOPPING CART: Check if add to cart button should show
    shouldShowAddToCartButton(row) {
      return (
        row.status === this.$t('toolStatus.free') &&
        !this.toolsInCart.includes(row.id) &&
        !this.cartActive &&
        this.enableMultiRentals &&
        row.car === '' &&
        !row.multiTool
      );
    },
    // SHOPPING CART: Check if remove from cart button should show
    shouldShowRemoveFromCartButton(row) {
      return (
        row.status === this.$t('toolStatus.free') &&
        this.toolsInCart.includes(row.id) &&
        this.cartActive &&
        this.enableMultiRentals
      );
    },
    // SHOPPING CART: Add tool to cart
    addToCart(index, row) {
      this.toolsInCart.push(row.id);
      localStorage.setItem(this.customerId + '-toolsInCart', JSON.stringify(this.toolsInCart));
      this.$toast.success(this.$t('tools.addedToCart'), { timeout: 1500 });
    },
    // SHOPPING CART: Remove tool from cart
    removeFromCart(index, row) {
      this.toolsInCart = this.toolsInCart.filter(x => x !== row.id);
      this.tools = this.tools.filter(x => x.id !== row.id);
      localStorage.setItem(this.customerId + '-toolsInCart', JSON.stringify(this.toolsInCart));
    },
    // SHOPPING CART: Toggle cart view
    toggleCart() {
      if (this.cartActive) {
        this.cartActive = false;
        this.cardTitle = this.$t('tools.tools');
      } else {
        this.cartActive = true;
        this.cardTitle = this.$t('tools.cart');
      }
      this.fetchData();
    },
    // SHOPPING CART: Open rent/loan form
    openRentLoanForm() {
      if (this.toolsInCart.length === 0) {
        this.$toast.danger(this.$t('tools.emptyCart'), { timeout: 0 });
      } else {
        this.showRentLoanForm = true;
      }
    },
    // SHOPPING CART: Close rent/loan form
    closeRentLoanForm() {
      this.showRentLoanForm = false;
    },
    // SHOPPING CART: Finish rent/loan and clear cart
    finishRentLoan() {
      this.toolsInCart = [];
      this.cardTitle = this.$t('tools.tools');
      localStorage.setItem(this.customerId + '-toolsInCart', JSON.stringify(this.toolsInCart));
      this.cartActive = false;
      this.fetchData();
    },
    handleSortChange({ prop, order }) {
      if (order === null) {
        this.sortBy = null;
        this.sortDescending = false;
      } else {
        const column = this.tableColumns.find(c => c.prop === prop);
        this.sortBy = column ? column.sortField : prop;
        this.sortDescending = order === 'descending';
      }
      this.pagination.currentPage = 1;
      this.fetchData();
    },
    // Filter methods
    setStatusFilter(value) {
      this.statusFilter = value;
      this.pagination.currentPage = 1;
      this.fetchData();
    },
    setToolTypeFilter(value) {
      this.toolTypeFilter = value;
      this.pagination.currentPage = 1;
      this.fetchData();
    },
    setLocationFilter(value) {
      this.locationFilter = value;
      Cookies.set(this.customerId + '-locationFilter', this.locationFilter, { expires: new Date(new Date().getTime() + 3 * 60 * 1000) });
      this.pagination.currentPage = 1;
      this.fetchData();
    },
    async fetchLocationsData() {
      try {
        const response = await api.get('/Location');
        this.locationOptions = [
          { id: 0, name: this.$t('locations.all') },
          ...response.data.result.map(item => ({ id: item.id, name: item.address }))
        ];
        this.locationOptionsAvailable = true;
      } catch (error) {
        console.error('API request error:', error); // eslint-disable-line no-console
        this.$toast.error(this.$t('common.unableToRetrieveData'), { timeout: 0 });
      }
    },
    navigateToUserProfile(userId) {
      this.$router.push({ name: 'User', params: { userId: userId } });
    },
    navigateToProject(projectId) {
      this.$router.push({ name: 'Project', params: { projectId: projectId } });
    },
    navigateToCar(carId) {
      this.$router.push({ name: 'Car', params: { toolGroupId: carId } });
    },
    handleClearSearch() {
      Cookies.remove('toolsPage');
      this.pagination.currentPage = 1;
      this.fetchData();
    },
    search(event) {
      const queryLength = this.searchQuery.length;
      this.pagination.currentPage = 1;
      if (event.key === 'Backspace' || event.key === 'Enter' || Number.isInteger(Number(this.searchQuery))) {
        this.fetchData();
      } else {
        if (queryLength > 1) {
          if (queryLength % 2 === 0) {
            this.fetchData();
          }
        }
      }
    },
    openCreateToolForm() {
      this.showCreateToolForm = true;
      this.$nextTick(() => {
        window.scrollTo(0, 0);
      });
    },
    checkMobile() {
      this.isMobile = window.innerWidth <= 1024;
    },
    closeCreateToolForm() {
      this.showCreateToolForm = false;
    },
    goToTool(item) {
      this.$router.push({ name: 'Tool', params: { toolId: item.id } });
    },
    updatePage(item) {
      this.pagination.currentPage = item;
      Cookies.set('toolsPage', this.pagination.currentPage, { expires: new Date(new Date().getTime() + 1 * 60 * 1000) });
      this.fetchData();
    },
    changePage(page) {
      if (page !== this.pagination.currentPage && page >= 1 && page <= this.totalPages) {
        this.pagination.currentPage = page;
        Cookies.set('toolsPage', this.pagination.currentPage, { expires: new Date(new Date().getTime() + 1 * 60 * 1000) });
        this.fetchData();
      }
    },
    nextPage() {
      if (this.pagination.currentPage < this.totalPages) {
        this.changePage(this.pagination.currentPage + 1);
      }
    },
    prevPage() {
      if (this.pagination.currentPage > 1) {
        this.changePage(this.pagination.currentPage - 1);
      }
    },
    getStatusTranslation(statusId) {
      switch (statusId) {
        case 1: return this.$t('toolStatus.free');
        case 2: return this.$t('toolStatus.inUse');
        case 3: return this.$t('toolStatus.broken');
        case 4: return this.$t('toolStatus.outOfUse');
        case 5: return this.$t('toolStatus.loaned');
        case 6: return this.$t('toolStatus.rented');
        case 7: return this.$t('toolStatus.partlyUsed');
        case 8: return this.$t('toolStatus.fullyUsed');
        default: return 'N/A';
      }
    },
    async deleteTool(item) {
      let deleteUrl = `/Tool/${item.id}`;
      try {
        await api.delete(deleteUrl);
        await swal.fire({
          title: this.$t('delete.deleted'),
          text: `${this.$t('delete.youDeleted')} ${item.name}`,
          icon: 'success',
          customClass: { confirmButton: 'btn btn-success btn-fill' },
          buttonsStyling: false
        });
        this.tools = this.tools.filter(i => i.id != item.id);
      } catch (error) {
        console.error('API request error:', error); // eslint-disable-line no-console
        this.$toast.danger(`${this.$t('common.unableToDelete')} ${item.name}`, { timeout: 0 });
      }
    },
    handleEdit(row) {
      this.$router.push({ name: 'Tool', params: { toolId: row.id, editMode: true } });
    },
    async handleDelete(row) {
      const result = await swal.fire({
        title: this.$t('delete.areYouSure'),
        text: this.$t('delete.noRevert'),
        icon: 'warning',
        showCancelButton: true,
        customClass: {
          confirmButton: 'btn btn-success btn-fill',
          cancelButton: 'btn btn-danger btn-fill'
        },
        confirmButtonText: this.$t('delete.yesDeleteIt'),
        cancelButtonText: this.$t('delete.cancel'),
        buttonsStyling: false
      });
      if (result.isConfirmed) {
        await this.deleteTool(row);
      }
    },
    async fetchData() {
      let statusUrlBit = '';
      if (this.statusFilter !== this.$t('toolStatus.all') && this.statusFilter !== '') {
        let id = this.statusOptions.filter(op => op.status === this.statusFilter)[0].id;
        statusUrlBit = `&toolStatusId=${id}`;
      }

      let toolTypeUrlBit = '';
      if (this.toolTypeFilter !== this.$t('tools.toolTypeStatus') && this.toolTypeFilter !== '') {
        let id = this.toolTypeOptions.filter(op => op.type === this.toolTypeFilter)[0].id;
        toolTypeUrlBit = `&toolTypeId=${id}`;
      }

      let locationUrlBit = '';
      if (this.allowLocationFilteringForTools && this.locationFilter !== this.$t('locations.all') && this.locationFilter !== '') {
        let id = this.locationOptions.find(op => op.name === this.locationFilter)?.id;
        locationUrlBit = id ? `&locationId=${id}` : '';
      }

      // SHOPPING CART: Add cart filtering
      let idsBit = '';
      if (this.cartActive) {
        if (this.toolsInCart.length === 0) {
          this.tools = [];
          this.pagination.total = 0;
          this.pagination.currentPage = 1;
          return;
        }
        idsBit = '&ids=' + this.toolsInCart.join(',');
      }

      let sortBit = '';
      if (this.sortBy) {
        sortBit = `&sortBy=${this.sortBy}&sortDescending=${this.sortDescending}`;
      }

      let toolsAvailableUrl = '';
      if (this.searchQuery === '') {
        Cookies.remove('toolsSearchQuery');
        toolsAvailableUrl = `/Tool?currentPage=${this.pagination.currentPage}&pageSize=${this.pagination.perPage}${statusUrlBit}${toolTypeUrlBit}${locationUrlBit}${idsBit}${sortBit}`;
      } else {
        Cookies.set('toolsSearchQuery', this.searchQuery, { expires: new Date(new Date().getTime() + 1 * 60 * 1000) });
        toolsAvailableUrl = `/Tool?currentPage=${this.pagination.currentPage}&pageSize=${this.pagination.perPage}&searchString=${this.searchQuery}${statusUrlBit}${toolTypeUrlBit}${idsBit}${sortBit}`;
      }

      try {
        const response = await api.get(toolsAvailableUrl);
        this.pagination.total = response.data.totalItems;
        this.tools = response.data.result.map(item => {
          let project = '';
          let projectId = '';
          let car = '';
          let carId = '';
          let carLicensePlate = '';
          let externalUser = '';
          let reservingUser = '';
          let reservingUserId = '';
          let totalUse = '';

          if (item.multiTool === true && item.inUse !== null && item.inUse !== 0) {
            totalUse = item.inUse + '/' + item.totalCount;
          } else {
            if (item.toolReservations && item.toolReservations.length > 0) {
              project = item.toolReservations[0].project.key;
              projectId = item.toolReservations[0].project.id;
              reservingUser = item.toolReservations[0].user.firstName;
              reservingUserId = item.toolReservations[0].user.id;
            }
            if (item.externalToolReservations && item.externalToolReservations.length > 0) {
              reservingUser = item.externalToolReservations[0].user.firstName;
              reservingUserId = item.externalToolReservations[0].user.id;
              externalUser = item.externalToolReservations[0].externalUser;
              if (item.externalToolReservations[0].project) {
                project = item.externalToolReservations[0].project.key;
                projectId = item.externalToolReservations[0].project.id;
              }
            }
            if (item.toolToToolGroup !== null) {
              car = item.toolToToolGroup.toolGroup.name;
              carId = item.toolToToolGroup.toolGroup.id;
              carLicensePlate = item.toolToToolGroup.toolGroup.toolGroupProperties.filter(p => p.key === 'licencePlate')[0]?.value || '';
            }
          }

          return {
            id: item.id,
            locationId: item.locationId,
            toolTypeId: this.toolTypeOptions.filter(op => op.id === item.toolTypeId)[0]?.type || 'Unknown',
            image: 'data:image/jpeg;base64,' + item.toolImageThumbnailBytes,
            status: this.getStatusTranslation(item.toolStatusId),
            name: item.name,
            customerToolId: '#' + item.customerToolId,
            description: item.description,
            sku: item.sku,
            dailyPrice: item.dailyPrice,
            brand: item.brandName,
            purchased: new Date(item.purchaseDate).toLocaleDateString('is-IS'),
            project: project,
            projectId: projectId,
            car: car,
            carId: carId,
            carLicensePlate: carLicensePlate,
            externalUser: externalUser,
            reservingUser: reservingUser,
            reservingUserId: reservingUserId,
            multiTool: item.multiTool,
            totalUse: totalUse,
            locationName: this.allowLocationFilteringForTools
              ? this.locationOptions.find(op => op.id === item.locationId)?.name || ''
              : ''
          };
        });
        this.toolsDataReady = true;
      } catch (error) {
        console.error('API request error:', error); // eslint-disable-line no-console
        this.$toast.danger(`Unable to retrieve tool data: ${error.message} <br> Please refresh the page`, { timeout: 0 });
      }
    },
    async fetchToolTypesData() {
      let getToolTypesUrl = `/ToolType`;
      try {
        const response = await api.get(getToolTypesUrl);
        this.toolTypeOptions.push(...response.data);
        await this.fetchData();
        this.toolTypeOptionsAvailable = true;
      } catch (error) {
        console.error('API request error:', error); // eslint-disable-line no-console
        this.$toast.danger(this.$t('common.unableToRetrieveData'), { timeout: 0 });
      }
    },
  }
};
</script>
<style scoped>
.mobile-row {
  display: flex;
  flex-direction: row;
  align-items: center;
}
.mobile-info-row {
  display: flex;
  align-items: center;
  min-width: 0;
}
.mobile-label {
  white-space: nowrap;
  margin-right: 4px;
  flex-shrink: 0;
}
.truncate-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.mobile-image {
  flex: 1;
}
.mobile-data {
  flex: 3;
  display: flex;
  flex-direction: column;
}
.status-select {
  padding-bottom: 20px;
}
.search-mobile {
  margin-bottom: 0;
}
.clickable-link {
  color: #007bff;
  cursor: pointer;
  text-decoration: underline;
}
.clickable-link-small-font {
  color: #007bff;
  cursor: pointer;
  text-decoration: underline;
  font-size: 12px;
}
.status-info {
  font-size: 12px;
}
.centered-button-group {
  display: flex;
  justify-content: center;
  align-items: center;
}
/* SHOPPING CART: Green button styles */
button.green,
button.green:hover,
button.green:active,
button.green:focus {
  color: green !important;
  background-color: transparent !important;
  border: none !important;
  box-shadow: none !important;
}
</style>
